import React, { useMemo, ReactNode, useState } from 'react';
import { UserLookup } from '../../../components/lookups/user-lookup/user-lookup';
import { IUserLookupProps } from '../../../components/lookups/user-lookup/interfaces';
import { TeamLookup } from '../../../components/lookups/team-lookup/team-lookup';
import { ITeamLookupProps } from '../../../components/lookups/team-lookup/interfaces';
import { moduleClientFactory } from '../../../api-service/module-client-factory';

export interface IUserAndTeamLookupProps {
  userProps: IUserLookupProps;
  teamProps: ITeamLookupProps;
  disabled?: boolean;
  render: (userInput: ReactNode, teamInput: ReactNode) => ReactNode;
}

// Component that handles the interaction between user and team, ie it
// automatically selects a team when a user is selected, and it clears
// the team when the user is cleared. It uses a render prop, so you
// pass in the props for the usual user and team lookups and provide a
// render function that takes the user and team components so you can
// render them however you like.
export function UserAndTeamLookup(props: IUserAndTeamLookupProps) {
  const { userProps, teamProps, render } = props;
  const { onChange: userOnChange, ...userRest } = userProps;
  const { onChange: teamOnChange, ...teamRest } = teamProps;

  const organizationClient = useMemo(() => moduleClientFactory.createOrganizationClient(), []);

  // This is a very dirty hack, but the wizard implementation makes it very hard to properly
  // update two fields at the same time, so this defers changing the team value until the
  // wizard reducer has run and we have the updated total state before we set the team.
  const [pendingTeamChange, setPendingTeamChange] = useState<{ teamId: string | undefined } | null>(null);

  if (pendingTeamChange) {
    setTimeout(() => teamOnChange?.(pendingTeamChange.teamId), 1);
    setPendingTeamChange(null);
  }

  const [pendingUserChange, setPendingUserChange] = useState<{ userId: string | undefined } | null>(null);

  if (pendingUserChange) {
    setTimeout(() => userOnChange?.(pendingUserChange.userId), 1);
    setPendingUserChange(null);
  }

  const handleUserChange = (userId?: string) => {
    userOnChange?.(userId);

    if (userId) {
      organizationClient.getUserById({ userId }).then(response => {
        setPendingTeamChange({ teamId: response.user?.teamId });
      });
    } else {
      setPendingTeamChange({ teamId: undefined });
    }
  };

  const handleTeamChange = (teamId?: string) => {
    teamOnChange?.(teamId);
    setPendingUserChange({ userId: undefined });
  };

  const userInput = <UserLookup onChange={handleUserChange} {...userRest} />;
  const teamInput = <TeamLookup onChange={handleTeamChange} {...teamRest} />;

  return <>{render(userInput, teamInput)}</>;
}
